import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Button,
  ButtonGroup,
  Checkbox,
  Divider,
  IconButton,
  InputNumber,
  Message,
  Modal,
  Panel,
  SelectPicker,
  Stack,
  Table,
  Tooltip,
  Whisper,
} from 'rsuite';

import {
  useActivityHourForecastForMonths,
  useActivityHourForecastMonthsWithHours,
  useActivityHourForecast,
  useActivityHourForecastTotals,
  useCloseDates,
  useProjectActivityForecastSummary,
  useTask,
  useBulkUpsertActivityHourForecast,
  useAddActivitySupporter,
  useSetActivitySupporters,
  useUsers,
} from '../hooks/useQueries';

import './ActivityHourForecastPage.css';

const { Column, HeaderCell, Cell } = Table;

const pad2 = (n) => String(n).padStart(2, '0');

// Use a local date string (YYYY-MM-DD) so month boundaries don't shift for users in timezones ahead of UTC.
const toLocalDateString = (d) => {
  if (!d) return null;
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return null;
  const yyyy = date.getFullYear();
  const mm = pad2(date.getMonth() + 1);
  const dd = pad2(date.getDate());
  return `${yyyy}-${mm}-${dd}`;
};

const parseLocalDateString = (yyyyMmDd) => {
  if (!yyyyMmDd || typeof yyyyMmDd !== 'string') return null;
  const m = yyyyMmDd.match(/^([0-9]{4})-([0-9]{2})-([0-9]{2})$/);
  if (!m) return null;
  const year = Number(m[1]);
  const monthIndex = Number(m[2]) - 1;
  const day = Number(m[3]);
  return new Date(year, monthIndex, day);
};

const formatMonthLabel = (monthStartIso) => {
  const d = parseLocalDateString(String(monthStartIso || '').slice(0, 10));
  if (!d) return '';
  const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = MONTHS_SHORT[d.getMonth()] || '';
  const yy = String(d.getFullYear()).slice(-2);
  return `${month} '${yy}`;
};

const getBaseWorkingHoursInMonth = (monthStartIso, hoursPerWorkingDay = 8) => {
  const d = parseLocalDateString(String(monthStartIso || '').slice(0, 10));
  if (!d) return null;

  const year = d.getFullYear();
  const monthIndex = d.getMonth();
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();

  let workingDays = 0;
  for (let day = 1; day <= daysInMonth; day += 1) {
    const dow = new Date(year, monthIndex, day).getDay();
    // 0=Sun, 6=Sat
    if (dow >= 1 && dow <= 5) workingDays += 1;
  }

  return workingDays * Number(hoursPerWorkingDay);
};

const parseMonthDayFromIso10 = (yyyyMmDd) => {
  const s = String(yyyyMmDd || '').slice(0, 10);
  const m = s.match(/^([0-9]{4})-([0-9]{2})-([0-9]{2})$/);
  if (!m) return null;
  const monthIndex = Number(m[2]) - 1;
  const day = Number(m[3]);
  if (!Number.isInteger(monthIndex) || monthIndex < 0 || monthIndex > 11) return null;
  if (!Number.isInteger(day) || day < 1 || day > 31) return null;
  return { monthIndex, day };
};

const formatMonthDayLabel = (monthIndex, day) => `${pad2(Number(monthIndex) + 1)}/${pad2(Number(day))}`;

const formatRate = (rate) => {
  if (rate === null || rate === undefined || rate === '') return '—';
  const n = Number(rate);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
};

const formatCost = (rate, hours) => {
  if (rate === null || rate === undefined || rate === '') return '—';
  const r = Number(rate);
  const h = Number(hours);
  if (!Number.isFinite(r) || !Number.isFinite(h)) return '—';
  return (r * h).toLocaleString(undefined, { style: 'currency', currency: 'USD' });
};

const buildRateSegments = (monthStarts, getRateForMonth) => {
  const segments = [];
  let prevKey = null;
  for (const ms of monthStarts) {
    const rate = getRateForMonth(ms);
    const key = rate === null || rate === undefined ? 'r:null' : Number.isFinite(Number(rate)) ? `r:${Number(rate)}` : 'r:null';
    const last = segments[segments.length - 1];

    if (!last || prevKey !== key) {
      segments.push({ start: ms, end: ms, rate });
      prevKey = key;
    } else {
      last.end = ms;
    }
  }
  return segments;
};

const buildRateSegmentsWithEffectiveDate = (monthStarts, getRateForMonth, getEffectiveDateForMonth) => {
  const segments = [];
  let prevKey = null;
  for (const ms of monthStarts) {
    const rate = getRateForMonth(ms);
    const eff = String(getEffectiveDateForMonth(ms) ?? '').slice(0, 10);
    const keyRate = rate === null || rate === undefined ? 'r:null' : Number.isFinite(Number(rate)) ? `r:${Number(rate)}` : 'r:null';
    const keyEff = /^\d{4}-\d{2}-\d{2}$/.test(eff) ? `e:${eff}` : 'e:null';
    const key = `${keyRate}|${keyEff}`;
    const last = segments[segments.length - 1];

    if (!last || prevKey !== key) {
      segments.push({ rate, effectiveDate: /^\d{4}-\d{2}-\d{2}$/.test(eff) ? eff : null });
      prevKey = key;
    }
  }
  return segments;
};

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '—';
  const n = Number(amount);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
};

const snapToQuarter = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return 0;
  return Math.round(v / 0.25) * 0.25;
};

const truncateText = (value, maxLen = 70) => {
  const s = String(value ?? '');
  if (s.length <= maxLen) return s;
  return `${s.slice(0, Math.max(0, maxLen - 1))}…`;
};

const WINDOW_SIZE = 6;
const PAST_FORECAST_OVERRIDE_CLICK_THRESHOLD = 5;

const toMonthStartIso = (d) => {
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return null;
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-01`;
};

const addMonths = (monthStartIso, deltaMonths) => {
  const d = parseLocalDateString(String(monthStartIso || '').slice(0, 10));
  if (!d) return null;
  const out = new Date(d.getFullYear(), d.getMonth() + Number(deltaMonths || 0), 1);
  return toLocalDateString(out);
};

const readStartFromSearchParams = (searchParams) => {
  const raw = String(searchParams?.get?.('start') || '').slice(0, 10);
  const parsed = parseLocalDateString(raw);
  if (!parsed) return null;
  return toMonthStartIso(parsed);
};

const normalizeMonthStart = (yyyyMmDd) => {
  const raw = String(yyyyMmDd || '').slice(0, 10);
  const parsed = parseLocalDateString(raw);
  if (!parsed) return null;
  return `${raw.slice(0, 7)}-01`;
};

const snapToHalfYearStart = (monthStartIso) => {
  const d = parseLocalDateString(String(monthStartIso || '').slice(0, 10));
  if (!d) return null;
  const year = d.getFullYear();
  const monthIndex = d.getMonth();
  const halfYearStartMonthIndex = monthIndex < 6 ? 0 : 6;
  return toLocalDateString(new Date(year, halfYearStartMonthIndex, 1));
};

export default function ActivityHourForecastPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const [printMode, setPrintMode] = useState(false);

  const projectIdParam = Number(params?.projectId);
  const taskOrderIdParam = Number(params?.taskOrderId);

  const taskId = Number(params?.taskId);
  const activityId = Number(params?.activityId);

  const [start, setStart] = useState(() => {
    const initial = readStartFromSearchParams(searchParams) ?? toMonthStartIso(new Date());
    return snapToHalfYearStart(initial) ?? initial;
  });
  const [filterMonthsWithHours, setFilterMonthsWithHours] = useState(false);

  useEffect(() => {
    const fromUrl = readStartFromSearchParams(searchParams);
    const desired = fromUrl ? (filterMonthsWithHours ? fromUrl : snapToHalfYearStart(fromUrl) ?? fromUrl) : null;
    if (desired && desired !== start) setStart(desired);
  }, [filterMonthsWithHours, searchParams, start]);

  const { data: task } = useTask(Number.isInteger(taskId) ? taskId : null);

  const projectId = Number.isInteger(Number(task?.projectId)) ? Number(task.projectId) : null;

  const { data: activitySummary } = useProjectActivityForecastSummary(projectId, null, {
    enabled: Number.isInteger(projectId),
  });

  const baseEnabled = Number.isInteger(taskId) && Number.isInteger(activityId) && !!start;

  const {
    data: monthsWithHoursRes,
    isLoading: monthsWithHoursLoading,
    isFetching: monthsWithHoursFetching,
  } = useActivityHourForecastMonthsWithHours(
    Number.isInteger(taskId) ? taskId : null,
    Number.isInteger(activityId) ? activityId : null,
    { enabled: baseEnabled && filterMonthsWithHours }
  );

  const monthsWithHours = useMemo(() => {
    const arr = Array.isArray(monthsWithHoursRes?.months) ? monthsWithHoursRes.months : [];
    return arr.map((m) => normalizeMonthStart(m)).filter(Boolean);
  }, [monthsWithHoursRes?.months]);

  const sparseMonthStarts = useMemo(() => {
    if (!filterMonthsWithHours) return [];
    if (monthsWithHours.length === 0) return [];

    const normalizedStart = normalizeMonthStart(start) ?? monthsWithHours[0];

    const exactIdx = monthsWithHours.indexOf(normalizedStart);
    const maxPageStart = Math.max(0, monthsWithHours.length - WINDOW_SIZE);

    let pageStartIdx = 0;
    if (exactIdx >= 0) {
      pageStartIdx = Math.min(maxPageStart, exactIdx);
    } else {
      const geIdx = monthsWithHours.findIndex((m) => m >= normalizedStart);
      if (geIdx === -1) pageStartIdx = maxPageStart;
      else pageStartIdx = Math.min(maxPageStart, Math.floor(geIdx / WINDOW_SIZE) * WINDOW_SIZE);
    }

    return monthsWithHours.slice(pageStartIdx, pageStartIdx + WINDOW_SIZE);
  }, [filterMonthsWithHours, monthsWithHours, start]);

  const {
    data: contiguousForecast,
    isLoading: contiguousForecastLoading,
    isFetching: contiguousForecastFetching,
    error: contiguousForecastError,
  } = useActivityHourForecast(
    Number.isInteger(taskId) ? taskId : null,
    Number.isInteger(activityId) ? activityId : null,
    String(start || '').slice(0, 10),
    WINDOW_SIZE,
    {
      enabled: baseEnabled && (!filterMonthsWithHours || sparseMonthStarts.length === 0),
    }
  );

  const {
    data: sparseForecast,
    isLoading: sparseForecastLoading,
    isFetching: sparseForecastFetching,
    error: sparseForecastError,
  } = useActivityHourForecastForMonths(
    Number.isInteger(taskId) ? taskId : null,
    Number.isInteger(activityId) ? activityId : null,
    sparseMonthStarts,
    {
      enabled: baseEnabled && filterMonthsWithHours && sparseMonthStarts.length > 0,
    }
  );

  const forecast = filterMonthsWithHours && sparseMonthStarts.length > 0 ? sparseForecast : contiguousForecast;
  const forecastLoading =
    filterMonthsWithHours && sparseMonthStarts.length > 0
      ? sparseForecastLoading
      : contiguousForecastLoading || (filterMonthsWithHours ? monthsWithHoursLoading : false);
  const forecastError = filterMonthsWithHours && sparseMonthStarts.length > 0 ? sparseForecastError : contiguousForecastError;

  const forecastFetching =
    filterMonthsWithHours && sparseMonthStarts.length > 0
      ? sparseForecastFetching
      : contiguousForecastFetching || (filterMonthsWithHours ? monthsWithHoursFetching : false);

  const forecastBusy = Boolean(forecastLoading || forecastFetching);

  const { data: allTimeTotals } = useActivityHourForecastTotals(
    Number.isInteger(taskId) ? taskId : null,
    Number.isInteger(activityId) ? activityId : null,
    { enabled: Number.isInteger(taskId) && Number.isInteger(activityId) }
  );

  const allTimeTotalHoursByUserId = useMemo(() => {
    const out = new Map();
    const list = Array.isArray(allTimeTotals?.rows) ? allTimeTotals.rows : [];
    for (const r of list) {
      const uid = Number(r?.userId);
      if (!Number.isInteger(uid)) continue;
      out.set(uid, Number(r?.totalHours ?? 0));
    }
    return out;
  }, [allTimeTotals?.rows]);

  const bulkUpsert = useBulkUpsertActivityHourForecast({
    // Keep UX quiet; the Save button spinner is enough.
  });

  const addSupporter = useAddActivitySupporter({
    // Toasts would be noisy here; the row list updating is enough.
  });

  const setSupporters = useSetActivitySupporters({
    // Toasts would be noisy here; the row list updating is enough.
  });

  const {
    data: usersRes,
    isLoading: usersLoading,
    error: usersError,
  } = useUsers();
  const userFteById = useMemo(() => {
    const out = new Map();
    const list = Array.isArray(usersRes) ? usersRes : [];
    for (const u of list) {
      const id = Number(u?.id);
      if (!Number.isInteger(id)) continue;
      const raw = Number(u?.ftePercent ?? u?.fte_percent ?? 100);
      const fte = Number.isFinite(raw) ? raw : 100;
      out.set(id, fte);
    }
    return out;
  }, [usersRes]);

  const contiguousMonthStarts = useMemo(() => {
    const fromServer = Array.isArray(contiguousForecast?.months) ? contiguousForecast.months : null;
    if (fromServer && fromServer.length > 0) {
      return fromServer
        .map((m) => String(m?.monthStart || '').slice(0, 10))
        .filter(Boolean)
        .slice(0, WINDOW_SIZE);
    }

    // Fallback: compute locally.
    const out = [];
    for (let i = 0; i < WINDOW_SIZE; i += 1) {
      const ms = addMonths(start, i);
      if (ms) out.push(ms);
    }
    return out;
  }, [contiguousForecast?.months, start]);

  const monthStartsForTable = useMemo(() => {
    if (filterMonthsWithHours && sparseMonthStarts.length > 0) return sparseMonthStarts;
    return contiguousMonthStarts;
  }, [contiguousMonthStarts, filterMonthsWithHours, sparseMonthStarts]);

  const currentMonthStartIso = useMemo(() => normalizeMonthStart(toMonthStartIso(new Date())), []);
  const [pastForecastOverrideEnabled, setPastForecastOverrideEnabled] = useState(false);
  const pastForecastUnlockAttemptCountRef = useRef(0);
  const [pastForecastOverrideModalOpen, setPastForecastOverrideModalOpen] = useState(false);

  const isMonthFullyInPast = (monthStartIso) => {
    const normalized = normalizeMonthStart(monthStartIso);
    if (!normalized || !currentMonthStartIso) return false;
    return normalized < currentMonthStartIso;
  };

  const isPastMonthLocked = (monthStartIso) => isMonthFullyInPast(monthStartIso) && !pastForecastOverrideEnabled;

  const registerPastForecastUnlockAttempt = () => {
    if (pastForecastOverrideEnabled) return;
    const next = Number(pastForecastUnlockAttemptCountRef.current || 0) + 1;
    if (next >= PAST_FORECAST_OVERRIDE_CLICK_THRESHOLD) {
      pastForecastUnlockAttemptCountRef.current = 0;
      setPastForecastOverrideModalOpen(true);
      return;
    }
    pastForecastUnlockAttemptCountRef.current = next;
  };

  const warningsReady = useMemo(() => {
    if (forecastBusy) return false;
    if (!forecast || !Array.isArray(forecast?.months)) return false;

    const forecastMonths = forecast.months
      .map((m) => normalizeMonthStart(m?.monthStart ?? m))
      .filter(Boolean);
    if (forecastMonths.length !== monthStartsForTable.length) return false;
    for (let i = 0; i < forecastMonths.length; i += 1) {
      if (forecastMonths[i] !== monthStartsForTable[i]) return false;
    }
    return true;
  }, [forecast, forecastBusy, monthStartsForTable]);

  const [warningsVisible, setWarningsVisible] = useState(false);
  const warningsTimerRef = useRef(null);

  const warningsKey = useMemo(() => {
    const baseStart = String(start || '').slice(0, 10);
    return `${taskId}|${activityId}|${baseStart}|${filterMonthsWithHours ? 1 : 0}|${monthStartsForTable.join(',')}`;
  }, [activityId, filterMonthsWithHours, monthStartsForTable, start, taskId]);

  // Never show warning icons during a range transition. Once the forecast is stable for a moment,
  // allow warnings to render. This avoids a brief flash when data is still settling.
  useEffect(() => {
    setWarningsVisible(false);
  }, [warningsKey]);

  useEffect(() => {
    if (warningsTimerRef.current) {
      clearTimeout(warningsTimerRef.current);
      warningsTimerRef.current = null;
    }

    if (!warningsReady) return;

    warningsTimerRef.current = setTimeout(() => {
      setWarningsVisible(true);
      warningsTimerRef.current = null;
    }, 300);

    return () => {
      if (warningsTimerRef.current) {
        clearTimeout(warningsTimerRef.current);
        warningsTimerRef.current = null;
      }
    };
  }, [warningsKey, warningsReady]);

  const rangeLabel = useMemo(() => {
    if (monthStartsForTable.length === 0) return '';
    const first = formatMonthLabel(monthStartsForTable[0]);
    const last = formatMonthLabel(monthStartsForTable[monthStartsForTable.length - 1]);
    return first && last ? `${first} – ${last}` : '';
  }, [monthStartsForTable]);

  const draftByKeyRef = useRef({});
  const draftInputByKeyRef = useRef({});
  const [draftVersion, setDraftVersion] = useState(0);
  const [savingAll, setSavingAll] = useState(false);

  const [editingSupports, setEditingSupports] = useState(false);
  const [addSupportUserId, setAddSupportUserId] = useState(null);
  const [removeSupportOpen, setRemoveSupportOpen] = useState(false);
  const [removeSupportUser, setRemoveSupportUser] = useState(null);

  const discardAllDrafts = () => {
    draftByKeyRef.current = {};
    draftInputByKeyRef.current = {};
    setDraftVersion((x) => x + 1);
  };

  const getDraftKey = (userId, monthStart) => `${userId}|${monthStart}`;

  const getRateForMonth = (row, monthStart) => {
    if (!row || !monthStart) return null;

    const ratesMap = row?.rates;
    const hasMonthRatesMap = Boolean(
      ratesMap && typeof ratesMap === 'object' && Object.keys(ratesMap).length > 0
    );

    if (hasMonthRatesMap) {
      const hasKey = Object.prototype.hasOwnProperty.call(ratesMap, monthStart);
      if (!hasKey) return null;
      const raw = ratesMap[monthStart];
      if (raw === null || raw === undefined || raw === '') return null;
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    }

    const fallback = Number(row?.rate);
    return Number.isFinite(fallback) ? fallback : null;
  };

  const getRateEffectiveDateForMonth = (row, monthStart) => {
    const s = String(row?.rateEffectiveDates?.[monthStart] ?? '').slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
  };

  const getEffectiveHours = (row, monthStart) => {
    const key = getDraftKey(row.userId, monthStart);
    const draft = draftByKeyRef.current[key];
    if (draft !== undefined) return draft;
    return Number(row?.hours?.[monthStart] ?? 0);
  };

  const getDisplayHours = (row, monthStart) => {
    const key = getDraftKey(row.userId, monthStart);
    if (key in draftInputByKeyRef.current) return draftInputByKeyRef.current[key];
    const draft = draftByKeyRef.current[key];
    if (draft !== undefined) return draft;
    return Number(row?.hours?.[monthStart] ?? 0);
  };

  const rows = useMemo(() => {
    const base = Array.isArray(forecast?.rows) ? forecast.rows : [];
    const normalized = base
      .filter((r) => Number.isInteger(Number(r?.userId)))
      .map((r) => ({
        userId: Number(r.userId),
        userName: r.userName || '—',
        role: r.role || 'support',
        rate: r.rate,
        rates: r.rates || {},
        rateEffectiveDates: r.rateEffectiveDates || r.rate_effective_dates || {},
        hours: r.hours || {},
        committedHours: r.committedHours || r.committed_hours || {},
      }));

    // Ensure lead row first.
    normalized.sort((a, b) => {
      const aLead = a.role === 'lead' ? 0 : 1;
      const bLead = b.role === 'lead' ? 0 : 1;
      if (aLead !== bLead) return aLead - bLead;
      return String(a.userName).localeCompare(String(b.userName));
    });

    // Totals row (computed using drafts too): both hours + cost.
    const totals = {};
    const totalCosts = {};
    for (const ms of monthStartsForTable) {
      const totalHours = normalized.reduce((sum, row) => sum + Number(getEffectiveHours(row, ms) ?? 0), 0);
      totals[ms] = totalHours;

      totalCosts[ms] = normalized.reduce((sum, row) => {
        const h = Number(getEffectiveHours(row, ms) ?? 0);
        const r = getRateForMonth(row, ms);
        if (!Number.isFinite(h) || !Number.isFinite(Number(r))) return sum;
        return sum + Number(r) * h;
      }, 0);
    }

    return [
      ...normalized,
      {
        userId: -1,
        userName: 'Total',
        role: 'total',
        rate: null,
        rates: {},
          rateEffectiveDates: {},
        hours: totals,
        costs: totalCosts,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forecast?.rows, monthStartsForTable, draftVersion]);

  const isTotalRow = (rowData) => Number(rowData?.userId) === -1 || rowData?.role === 'total';

  const commitCell = (row, monthStart) => {
    if (isTotalRow(row)) return;
    if (isPastMonthLocked(monthStart)) return;

    const key = getDraftKey(row.userId, monthStart);
    const raw = key in draftInputByKeyRef.current ? draftInputByKeyRef.current[key] : undefined;
    const parsed = raw === '' || raw === null || raw === undefined ? 0 : Number(raw);
    const normalized = Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
    const snapped = snapToQuarter(normalized);

    const current = Number(row?.hours?.[monthStart] ?? 0);
    if (snapped === current) {
      delete draftByKeyRef.current[key];
      delete draftInputByKeyRef.current[key];
    } else {
      draftByKeyRef.current[key] = snapped;
      draftInputByKeyRef.current[key] = snapped;
    }
    setDraftVersion((x) => x + 1);
  };

  const onChangeCell = (row, monthStart, value) => {
    if (isTotalRow(row)) return;
    if (isPastMonthLocked(monthStart)) return;

    const raw = value;
    const key = getDraftKey(row.userId, monthStart);
    draftInputByKeyRef.current[key] = raw;

    const v = raw === '' || raw === null || raw === undefined ? 0 : Number(raw);
    const normalized = Number.isFinite(v) ? Math.max(0, v) : 0;
    const snapped = snapToQuarter(normalized);

    const current = Number(row?.hours?.[monthStart] ?? 0);

    if (snapped === current) delete draftByKeyRef.current[key];
    else draftByKeyRef.current[key] = snapped;

    setDraftVersion((x) => x + 1);
  };

  const dirtyEdits = useMemo(() => {
    const base = Array.isArray(forecast?.rows) ? forecast.rows : [];
    const dirty = [];
    const drafts = draftByKeyRef.current;

    for (const row of base) {
      const userId = Number(row?.userId);
      if (!Number.isInteger(userId)) continue;

      for (const ms of monthStartsForTable) {
        if (isPastMonthLocked(ms)) continue;

        const key = getDraftKey(userId, ms);
        if (!(key in drafts)) continue;

        const next = Number(drafts[key] ?? 0);
        const current = Number(row?.hours?.[ms] ?? 0);
        if (next === current) continue;

        dirty.push({ userId, monthStart: ms, hours: Number.isFinite(next) ? next : 0 });
      }
    }

    return dirty;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forecast?.rows, monthStartsForTable, draftVersion, currentMonthStartIso, pastForecastOverrideEnabled]);

  const hasUnsavedChanges = dirtyEdits.length > 0;

  // In sparse mode, keep the URL/start aligned to the current page start so Prev/Next paging works.
  useEffect(() => {
    if (!filterMonthsWithHours) return;
    if (hasUnsavedChanges) return;
    const desired = sparseMonthStarts?.[0];
    if (!desired) return;

    const current = String(start || '').slice(0, 10);
    if (current === desired) return;

    setStart(desired);
    const next = new URLSearchParams(searchParams);
    next.set('start', desired);
    setSearchParams(next, { replace: true });
  }, [filterMonthsWithHours, hasUnsavedChanges, searchParams, setSearchParams, sparseMonthStarts, start]);

  const confirmDiscard = () => {
    if (!hasUnsavedChanges) return true;
    const ok = window.confirm('You have unsaved changes. Discard them and continue?');
    if (ok) discardAllDrafts();
    return ok;
  };

  const leadUserId = useMemo(() => {
    const lead = rows.find((r) => !isTotalRow(r) && r?.role === 'lead');
    const id = Number(lead?.userId);
    return Number.isInteger(id) ? id : null;
  }, [rows]);

  const supportUserIds = useMemo(() => {
    return rows
      .filter((r) => !isTotalRow(r) && r?.role !== 'lead')
      .map((r) => Number(r?.userId))
      .filter((id) => Number.isInteger(id));
  }, [rows]);

  const addSupportOptions = useMemo(() => {
    const existing = new Set(supportUserIds);
    const lead = Number.isInteger(leadUserId) ? Number(leadUserId) : null;

    const list = Array.isArray(usersRes) ? usersRes : [];

    return list
      .filter((u) => {
        const id = Number(u?.id);
        if (!Number.isInteger(id)) return false;
        if (Number.isInteger(lead) && id === lead) return false;
        if (existing.has(id)) return false;
        return true;
      })
      .map((u) => ({ label: u.name, value: Number(u.id) }));
  }, [leadUserId, supportUserIds, usersRes]);

  const enterSupportEditMode = () => {
    if (editingSupports) return;
    if (!confirmDiscard()) return;
    setEditingSupports(true);
  };

  const exitSupportEditMode = () => {
    setEditingSupports(false);
    setAddSupportUserId(null);
    setRemoveSupportOpen(false);
    setRemoveSupportUser(null);
  };

  const requestRemoveSupport = (rowData) => {
    if (!confirmDiscard()) return;
    const userId = Number(rowData?.userId);
    if (!Number.isInteger(userId)) return;
    if (rowData?.role === 'lead') return;
    if (isTotalRow(rowData)) return;

    setRemoveSupportUser({ userId, userName: rowData?.userName || '—' });
    setRemoveSupportOpen(true);
  };

  const confirmRemoveSupport = async () => {
    if (!confirmDiscard()) return;
    const uid = Number(removeSupportUser?.userId);
    if (!Number.isInteger(taskId) || !Number.isInteger(activityId) || !Number.isInteger(uid)) return;

    const nextUserIds = supportUserIds.filter((id) => id !== uid);
    await setSupporters.mutateAsync({ taskId, activityId, userIds: nextUserIds });

    setRemoveSupportOpen(false);
    setRemoveSupportUser(null);
  };

  const submitAddSupport = async () => {
    if (!confirmDiscard()) return;
    const uid = Number(addSupportUserId);
    if (!Number.isInteger(taskId) || !Number.isInteger(activityId) || !Number.isInteger(uid)) return;

    await addSupporter.mutateAsync({ taskId, activityId, userId: uid });
    setAddSupportUserId(null);
  };

  const setStartWithConfirm = (nextStart) => {
    let ns = String(nextStart || '').slice(0, 10);
    ns = normalizeMonthStart(ns) ?? ns;
    if (!filterMonthsWithHours) ns = snapToHalfYearStart(ns) ?? ns;
    if (!ns) return;
    if (ns === start) return;
    if (!confirmDiscard()) return;

    setStart(ns);
    const next = new URLSearchParams(searchParams);
    next.set('start', ns);
    setSearchParams(next, { replace: true });
  };

  const goPrevMonths = () => {
    if (filterMonthsWithHours && monthsWithHours.length > 0) {
      const normalizedStart = normalizeMonthStart(start);
      const currentIdx = normalizedStart ? monthsWithHours.indexOf(normalizedStart) : -1;
      const maxPageStart = Math.max(0, monthsWithHours.length - WINDOW_SIZE);
      const currentPageStart = currentIdx >= 0 ? Math.min(maxPageStart, currentIdx) : 0;
      const nextPageStart = Math.max(0, currentPageStart - WINDOW_SIZE);
      setStartWithConfirm(monthsWithHours[nextPageStart]);
      return;
    }

    const ns = addMonths(start, -WINDOW_SIZE);
    if (ns) setStartWithConfirm(ns);
  };

  const goNextMonths = () => {
    if (filterMonthsWithHours && monthsWithHours.length > 0) {
      const normalizedStart = normalizeMonthStart(start);
      const currentIdx = normalizedStart ? monthsWithHours.indexOf(normalizedStart) : -1;
      const maxPageStart = Math.max(0, monthsWithHours.length - WINDOW_SIZE);
      const currentPageStart = currentIdx >= 0 ? Math.min(maxPageStart, currentIdx) : 0;
      const nextPageStart = Math.min(maxPageStart, currentPageStart + WINDOW_SIZE);
      setStartWithConfirm(monthsWithHours[nextPageStart]);
      return;
    }

    const ns = addMonths(start, WINDOW_SIZE);
    if (ns) setStartWithConfirm(ns);
  };

  const goCurrentMonths = () => {
    if (filterMonthsWithHours && monthsWithHours.length > 0) {
      const current = toMonthStartIso(new Date());
      const normalizedCurrent = normalizeMonthStart(current) ?? monthsWithHours[0];
      const maxPageStart = Math.max(0, monthsWithHours.length - WINDOW_SIZE);

      let idx = monthsWithHours.findIndex((m) => m >= normalizedCurrent);
      if (idx === -1) idx = maxPageStart;
      const pageStart = Math.min(maxPageStart, Math.floor(idx / WINDOW_SIZE) * WINDOW_SIZE);
      setStartWithConfirm(monthsWithHours[pageStart]);
      return;
    }

    const ns = snapToHalfYearStart(toMonthStartIso(new Date()));
    if (ns) setStartWithConfirm(ns);
  };

  const isOnCurrentWindow = useMemo(() => {
    const current = toMonthStartIso(new Date());
    const normalizedCurrent = normalizeMonthStart(current);
    const normalizedStart = normalizeMonthStart(start);

    if (!filterMonthsWithHours) {
      const expectedStart = normalizedCurrent ? snapToHalfYearStart(normalizedCurrent) : null;
      return normalizedStart && expectedStart ? normalizedStart === expectedStart : false;
    }

    if (!normalizedCurrent || monthsWithHours.length === 0) return false;

    const maxPageStart = Math.max(0, monthsWithHours.length - WINDOW_SIZE);
    let idx = monthsWithHours.findIndex((m) => m >= normalizedCurrent);
    if (idx === -1) idx = maxPageStart;
    const pageStart = Math.min(maxPageStart, Math.floor(idx / WINDOW_SIZE) * WINDOW_SIZE);
    const expectedStart = monthsWithHours[pageStart];
    return normalizedStart === expectedStart;
  }, [filterMonthsWithHours, monthsWithHours, start]);

  const saveAll = async () => {
    if (!hasUnsavedChanges) return;
    if (!Number.isInteger(taskId) || !Number.isInteger(activityId)) return;
    if (savingAll) return;

    // Temporary development override should only last until the next save action.
    setPastForecastOverrideEnabled(false);
    pastForecastUnlockAttemptCountRef.current = 0;
    setPastForecastOverrideModalOpen(false);

    setSavingAll(true);
    try {
      await bulkUpsert.mutateAsync({ taskId, activityId, edits: dirtyEdits });

      for (const edit of dirtyEdits) {
        delete draftByKeyRef.current[getDraftKey(edit.userId, edit.monthStart)];
        delete draftInputByKeyRef.current[getDraftKey(edit.userId, edit.monthStart)];
      }
      setDraftVersion((x) => x + 1);
    } finally {
      setSavingAll(false);
    }
  };

  // Warn on browser refresh/close.
  useEffect(() => {
    const handler = (e) => {
      if (!hasUnsavedChanges) return;
      e.preventDefault();
      // Chrome requires returnValue to be set.
      // eslint-disable-next-line no-param-reassign
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  const activityOptions = useMemo(() => {
    const arr = Array.isArray(activitySummary?.activities) ? activitySummary.activities : [];

    if (!Number.isInteger(taskId)) return [];

    // Match Task Detail "#" ordering for activities on the current task.
    const orderByActivityId = new Map();
    const taskActivities = Array.isArray(task?.activities) ? task.activities : [];
    for (let i = 0; i < taskActivities.length; i += 1) {
      const id = Number(taskActivities[i]?.id);
      if (Number.isInteger(id)) orderByActivityId.set(id, i);
    }

    return arr
      .filter(
        (a) =>
          Number.isInteger(Number(a?.taskId)) &&
          Number.isInteger(Number(a?.activityId)) &&
          Number(a?.taskId) === Number(taskId)
      )
      .map((a) => {
        const totalHours = Number(a?.totalHours ?? 0);
        const lead = a?.leadName ? String(a.leadName) : '—';
        const desc = `Lead: ${lead} • ${Number.isFinite(totalHours) ? totalHours.toFixed(2) : '0.00'}h forecasted`;
        return {
          label: a?.activityName ? String(a.activityName) : `Activity #${Number(a.activityId)}`,
          value: `${Number(a.taskId)}|${Number(a.activityId)}`,
          description: desc,
          sortActivityId: Number(a.activityId),
          activityNumber: orderByActivityId.has(Number(a.activityId)) ? orderByActivityId.get(Number(a.activityId)) + 1 : null,
        };
      })
      .sort((a, b) => {
        const aIdx = orderByActivityId.get(Number(a.sortActivityId)) ?? Number.MAX_SAFE_INTEGER;
        const bIdx = orderByActivityId.get(Number(b.sortActivityId)) ?? Number.MAX_SAFE_INTEGER;
        if (aIdx !== bIdx) return aIdx - bIdx;
        return String(a.label).localeCompare(String(b.label));
      });
  }, [activitySummary?.activities, task?.activities, taskId]);

  const ACTIVITY_TOGGLE_WIDTH = 460;


  const selectedValue = useMemo(() => {
    if (!Number.isInteger(taskId) || !Number.isInteger(activityId)) return null;
    return `${taskId}|${activityId}`;
  }, [activityId, taskId]);

  const { data: closeDatesRes } = useCloseDates();
  const closeDates = useMemo(() => (Array.isArray(closeDatesRes) ? closeDatesRes : []), [closeDatesRes]);

  const closeCountByMonthStart = useMemo(() => {
    const defs = [];
    for (const cd of closeDates) {
      const dateIso = String(cd?.closeDate ?? cd?.close_date ?? '').slice(0, 10);
      const md = parseMonthDayFromIso10(dateIso);
      if (md) defs.push(md);
    }

    const out = new Map();
    for (const ms of monthStartsForTable) {
      const d = parseLocalDateString(String(ms || '').slice(0, 10));
      if (!d) continue;
      const year = d.getFullYear();
      const monthIndex = d.getMonth();
      const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();

      const count = defs.reduce((sum, md) => {
        if (md.monthIndex !== monthIndex) return sum;
        if (md.day > daysInMonth) return sum;
        return sum + 1;
      }, 0);

      out.set(String(ms).slice(0, 10), count);
    }

    return out;
  }, [closeDates, monthStartsForTable]);

  const closeDatesByMonthStart = useMemo(() => {
    const defs = [];
    for (const cd of closeDates) {
      const dateIso = String(cd?.closeDate ?? cd?.close_date ?? '').slice(0, 10);
      const md = parseMonthDayFromIso10(dateIso);
      if (!md) continue;
      defs.push({
        monthIndex: md.monthIndex,
        day: md.day,
        label: formatMonthDayLabel(md.monthIndex, md.day),
        description: String(cd?.description ?? '').trim(),
      });
    }

    const out = new Map();
    for (const ms of monthStartsForTable) {
      const d = parseLocalDateString(String(ms || '').slice(0, 10));
      if (!d) continue;
      const year = d.getFullYear();
      const monthIndex = d.getMonth();
      const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();

      const list = defs
        .filter((x) => x.monthIndex === monthIndex && x.day <= daysInMonth)
        .sort((a, b) => a.day - b.day);

      out.set(String(ms).slice(0, 10), list);
    }

    return out;
  }, [closeDates, monthStartsForTable]);

  const workableHoursByMonthStart = useMemo(() => {
    const out = new Map();
    for (const ms of monthStartsForTable) {
      const base = getBaseWorkingHoursInMonth(ms);
      const closeCount = Number(closeCountByMonthStart.get(ms) ?? 0);
      const hours = Number.isFinite(base) ? Math.max(0, Number(base) - closeCount * 8) : null;
      out.set(ms, hours);
    }
    return out;
  }, [closeCountByMonthStart, monthStartsForTable]);

  const printDisplayedTotals = useMemo(() => {
    const totalRow = rows.find((r) => isTotalRow(r));
    if (!totalRow) return { displayedHours: 0, displayedCost: 0 };

    let displayedHours = 0;
    let displayedCost = 0;
    for (const ms of monthStartsForTable) {
      const h = Number(totalRow?.hours?.[ms] ?? 0);
      const c = Number(totalRow?.costs?.[ms] ?? 0);
      displayedHours += Number.isFinite(h) ? h : 0;
      displayedCost += Number.isFinite(c) ? c : 0;
    }

    return { displayedHours, displayedCost };
  }, [monthStartsForTable, rows]);

  const selectedActivity = useMemo(() => {
    if (!selectedValue) return null;
    return activityOptions.find((o) => String(o?.value) === String(selectedValue)) ?? null;
  }, [activityOptions, selectedValue]);

  const selectedActivityLabel = useMemo(() => {
    if (!selectedActivity) return '';
    const n = Number(selectedActivity?.activityNumber);
    const num = Number.isInteger(n) ? `#${n} ` : '';
    return `${num}${selectedActivity?.label ?? ''}`.trim();
  }, [selectedActivity]);

  useEffect(() => {
    if (!printMode) return undefined;

    const handleAfterPrint = () => setPrintMode(false);
    window.addEventListener('afterprint', handleAfterPrint);

    // Ensure the print layout is rendered before printing.
    const t = setTimeout(() => {
      try {
        window.print();
      } catch {
        setPrintMode(false);
      }
    }, 50);

    return () => {
      clearTimeout(t);
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, [printMode]);

  return (
    <div className="ppc-page">
      <div className="ppc-noprint">
        <div className="ppc-forecast-header">
          <div className="ppc-forecast-header-left">
            <h3 style={{ margin: 0, lineHeight: '22px' }}>Forecast Hours</h3>
          </div>

          <div className="ppc-forecast-header-right">
            <ButtonGroup>
              {hasUnsavedChanges ? (
                <Button appearance="primary" loading={savingAll} disabled={savingAll} onClick={saveAll}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontWeight: 600 }}>Save</span>
                    <i className="bi bi-floppy" aria-hidden="true" />
                  </span>
                </Button>
              ) : null}
              <Button
                appearance="ghost"
                onClick={() => {
                  if (!confirmDiscard()) return;
                  const backFromActuals =
                    location?.state?.fromActuals === true && typeof location?.state?.actualsPath === 'string'
                      ? String(location.state.actualsPath)
                      : null;
                  if (backFromActuals) {
                    navigate(backFromActuals);
                    return;
                  }

                  const pid = Number.isInteger(projectIdParam)
                    ? projectIdParam
                    : Number.isInteger(Number(task?.projectId))
                      ? Number(task.projectId)
                      : null;
                  const toid = Number.isInteger(taskOrderIdParam)
                    ? taskOrderIdParam
                    : Number.isInteger(Number(task?.taskOrderId))
                      ? Number(task.taskOrderId)
                      : null;

                  if (Number.isInteger(pid) && Number.isInteger(toid) && Number.isInteger(taskId)) {
                    navigate(`/projects/${pid}/task-orders/${toid}/tasks/${taskId}`);
                    return;
                  }
                  if (Number.isInteger(taskId)) navigate(`/tasks/${taskId}`);
                  else navigate('/');
                }}
              >
                Back
              </Button>
            </ButtonGroup>
          </div>
        </div>

        <Divider />

        {forecastError ? (
          <Message type="error" showIcon>
            Failed to load hour forecast.
          </Message>
        ) : null}

        {usersError ? (
          <Message type="warning" showIcon>
            % committed is using 0% reserved hours (failed to load users).
          </Message>
        ) : null}

        <Panel
          bordered
          header={
            <Stack justifyContent="space-between" alignItems="center" wrap>
              <SelectPicker
                className="ppc-activity-picker"
                searchable
                cleanable={false}
                data={activityOptions}
                value={selectedValue}
                placeholder="Select activity"
                renderValue={(value, item) => {
                  const n = Number(item?.activityNumber);
                  const num = Number.isInteger(n) ? `#${n} ` : '';
                  // Must be a string; returning a ReactNode can show up as "[object Object]" while searching.
                  const full = `Activity: ${num}${item?.label ?? ''}`;
                  return truncateText(full, 80);
                }}
                style={{ width: '100%', minWidth: 200, maxWidth: ACTIVITY_TOGGLE_WIDTH }}
                popupClassName="ppc-activity-picker-popup"
                popupStyle={{
                  width: ACTIVITY_TOGGLE_WIDTH,
                  maxWidth: 'min(680px, calc(100vw - 40px))',
                }}
                renderOption={(label, item) => {
                  const n = Number(item?.activityNumber);
                  const num = Number.isInteger(n) ? `#${n} ` : '';
                  return (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        padding: '2px 0',
                        width: '100%',
                        maxWidth: '100%',
                        overflow: 'hidden',
                      }}
                    >
                      <div className="ppc-activity-option-title" title={`${num}${label}`}>
                        {num}
                        {label}
                      </div>
                      <div style={{ fontSize: 12, color: '#666', whiteSpace: 'normal' }}>{String(item?.description || '')}</div>
                    </div>
                  );
                }}
                onChange={(v) => {
                  if (!confirmDiscard()) return;
                  const raw = String(v || '');
                  const parts = raw.split('|');
                  const nextTaskId = Number(parts[0]);
                  const nextActivityId = Number(parts[1]);
                  if (Number.isInteger(nextTaskId) && Number.isInteger(nextActivityId)) {
                    navigate(
                      `/tasks/${nextTaskId}/activities/${nextActivityId}/forecast?start=${encodeURIComponent(
                        String(start || '').slice(0, 10)
                      )}`,
                      { state: location?.state }
                    );
                  }
                }}
              />

              <div className="ppc-forecast-range">
                <div className="ppc-forecast-range-row">
                  <Checkbox
                    className="ppc-forecast-filter"
                    checked={filterMonthsWithHours}
                    onChange={(_, checked) => {
                      const next = !!checked;
                      if (next === filterMonthsWithHours) return;
                      if (!confirmDiscard()) return;
                      setFilterMonthsWithHours(next);
                    }}
                    style={{ fontSize: 12, color: '#444' }}
                  >
                    Filter for months with hours
                  </Checkbox>
                  <IconButton
                    appearance="subtle"
                    size="sm"
                    icon={<i className="bi bi-chevron-left" aria-hidden="true" />}
                    aria-label="Previous months"
                    title="Previous months"
                    onClick={goPrevMonths}
                  />
                  <div className="ppc-forecast-range-label">{rangeLabel}</div>
                  <IconButton
                    appearance="subtle"
                    size="sm"
                    icon={<i className="bi bi-chevron-right" aria-hidden="true" />}
                    aria-label="Next months"
                    title="Next months"
                    onClick={goNextMonths}
                  />
                  <Button appearance="subtle" size="sm" onClick={goCurrentMonths} disabled={isOnCurrentWindow}>
                    Current
                  </Button>
                </div>
              </div>
            </Stack>
          }
        >
        {filterMonthsWithHours && !monthsWithHoursLoading && monthsWithHours.length === 0 ? (
          <Message type="info" showIcon style={{ marginBottom: 10 }}>
            No months have forecast hours yet.
          </Message>
        ) : null}

        {editingSupports ? (
          <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 12, color: '#666', fontWeight: 700 }}>Edit supports</div>
              <SelectPicker
                searchable
                cleanable
                size="sm"
                placeholder={
                  usersLoading
                    ? 'Loading users…'
                    : addSupportOptions.length === 0
                      ? 'No more users to add'
                      : 'Add support…'
                }
                data={addSupportOptions}
                value={Number.isInteger(addSupportUserId) ? addSupportUserId : null}
                onChange={(v) => setAddSupportUserId(Number.isInteger(v) ? v : null)}
                style={{ width: 240 }}
                disabled={usersLoading || addSupportOptions.length === 0}
              />
              <Button
                size="sm"
                appearance="primary"
                onClick={submitAddSupport}
                loading={addSupporter.isPending}
                disabled={addSupporter.isPending || !Number.isInteger(addSupportUserId)}
              >
                Add
              </Button>
              <div style={{ fontSize: 12, color: '#777' }}>Click the red minus to remove a support.</div>
            </div>

            <Button size="sm" appearance="subtle" onClick={exitSupportEditMode}>
              Done
            </Button>
          </div>
        ) : null}

        <Table
          data={rows}
          autoHeight
          bordered
          cellBordered
          loading={forecastLoading}
          rowKey={(row) => `${row.userId}`}
          rowHeight={66}
          headerHeight={38}
          style={{ width: '100%' }}
        >
          <Column width={160} fixed>
            <HeaderCell>
              <div className="ppc-forecast-user-header">
                <span>User</span>
                <span className="ppc-forecast-user-header__actions">
                  {!editingSupports ? (
                    <button
                      type="button"
                      className="ppc-forecast-user-header__icon"
                      title="Edit supports"
                      aria-label="Edit supports"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        enterSupportEditMode();
                      }}
                    >
                      <i className="bi bi-pencil" aria-hidden="true" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="ppc-forecast-user-header__icon"
                      style={{ opacity: 1, pointerEvents: 'auto' }}
                      title="Done"
                      aria-label="Done"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        exitSupportEditMode();
                      }}
                    >
                      <i className="bi bi-check-lg" aria-hidden="true" />
                    </button>
                  )}
                </span>
              </div>
            </HeaderCell>
            <Cell>
              {(rowData) => {
                if (isTotalRow(rowData)) return <span style={{ fontWeight: 800 }}>Total</span>;
                const role = rowData?.role === 'lead' ? 'Lead' : 'Support';
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', textAlign: 'center' }}>
                    <div
                      style={{
                        position: 'relative',
                        fontWeight: 700,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {editingSupports && rowData?.role !== 'lead' ? (
                        <button
                          type="button"
                          className="ppc-forecast-support-remove"
                          title="Remove support"
                          aria-label="Remove support"
                          disabled={setSupporters.isPending}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            requestRemoveSupport(rowData);
                          }}
                        >
                          <i className="bi bi-dash-circle-fill" aria-hidden="true" />
                        </button>
                      ) : null}
                      <span>{rowData?.userName || '—'}</span>
                    </div>
                    <div style={{ fontSize: 11, color: '#777' }}>{role}</div>
                  </div>
                );
              }}
            </Cell>
          </Column>

          <Column width={140} fixed>
            <HeaderCell>Rate</HeaderCell>
            <Cell>
              {(rowData) => {
                if (isTotalRow(rowData)) return <span style={{ fontWeight: 800 }}>—</span>;

                const isProjectForecast = Number.isInteger(Number(forecast?.projectId));
                const isMissingRateForMonth = (ms) => {
                  const r = getRateForMonth(rowData, ms);
                  if (r === null) return true;

                  // When forecasting within a project, a base (user) rate may be used as a fallback.
                  // We still want to warn if there is no project-specific effective-dated rate for the month.
                  if (isProjectForecast) {
                    const eff = getRateEffectiveDateForMonth(rowData, ms);
                    return eff === null;
                  }

                  return false;
                };

                const hasMissingRate = monthStartsForTable.some((ms) => isMissingRateForMonth(ms));
                const missingTooltipText = isProjectForecast
                  ? 'Missing project rate for one or more months (click to set rates)'
                  : 'Missing rate for one or more months (click to set rates)';

                const showMissingRateWarning = warningsVisible && hasMissingRate;

                const segments = buildRateSegmentsWithEffectiveDate(
                  monthStartsForTable,
                  (ms) => getRateForMonth(rowData, ms),
                  (ms) => getRateEffectiveDateForMonth(rowData, ms)
                );
                const displaySegments = segments.slice(0, 3);

                return (
                  <div
                    style={{
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      gap: 2,
                      height: '100%',
                      paddingRight: 18,
                    }}
                  >
                    {showMissingRateWarning ? (
                      <div style={{ position: 'absolute', top: 2, right: 2 }}>
                        <Whisper placement="topEnd" trigger="hover" speaker={<Tooltip>{missingTooltipText}</Tooltip>}>
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const uid = Number(rowData?.userId);
                              if (Number.isInteger(uid)) {
                                navigate(`/users/${uid}#rates`);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation();
                                const uid = Number(rowData?.userId);
                                if (Number.isInteger(uid)) {
                                  navigate(`/users/${uid}#rates`);
                                }
                              }
                            }}
                            aria-label={missingTooltipText}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#fa8c16',
                              cursor: 'pointer',
                              lineHeight: 1,
                            }}
                          >
                            <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />
                          </span>
                        </Whisper>
                      </div>
                    ) : null}
                    {displaySegments.map((seg) => {
                      return (
                        <div key={`${String(seg.effectiveDate)}-${String(seg.rate)}`} style={{ lineHeight: '16px' }}>
                          <div style={{ fontWeight: 700 }}>{formatRate(seg.rate)}</div>
                          <div style={{ fontSize: 11, color: '#777' }}>Eff: {seg.effectiveDate || '—'}</div>
                        </div>
                      );
                    })}
                    {segments.length > displaySegments.length ? (
                      <div style={{ fontSize: 11, color: '#777' }}>…</div>
                    ) : null}
                  </div>
                );
              }}
            </Cell>
          </Column>

          {monthStartsForTable.map((ms) => (
            <Column key={ms} width={170} flexGrow={1} align="right">
              <HeaderCell>
                {(() => {
                  const base = getBaseWorkingHoursInMonth(ms);
                  const closeCount = Number(closeCountByMonthStart.get(ms) ?? 0);
                  const hours = Number.isFinite(base) ? Math.max(0, Number(base) - closeCount * 8) : null;
                  const prefix = Number.isFinite(hours) ? `(${Math.round(hours)} hrs) - ` : '';
                  const monthCloseDates = closeDatesByMonthStart.get(ms) ?? [];
                  const tooltipText =
                    monthCloseDates.length > 0
                      ? monthCloseDates
                          .map((cd) => (cd.description ? `${cd.label} — ${cd.description}` : cd.label))
                          .join('\n')
                      : 'No close dates.';
                  return (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, whiteSpace: 'nowrap' }}>
                      {prefix ? (
                        <Whisper
                          trigger="hover"
                          placement="top"
                          enterable
                          speaker={
                            <Tooltip>
                              <div style={{ whiteSpace: 'pre-line', maxWidth: 360 }}>{tooltipText}</div>
                            </Tooltip>
                          }
                        >
                          <span style={{ fontWeight: 700, color: '#666', cursor: 'help' }}>{prefix}</span>
                        </Whisper>
                      ) : (
                        <span style={{ fontWeight: 700, color: '#666' }}>{prefix}</span>
                      )}
                      <span>{formatMonthLabel(ms)}</span>
                    </div>
                  );
                })()}
              </HeaderCell>
              <Cell>
                {(rowData) => {
                  const isTotal = isTotalRow(rowData);

                  if (isTotal) {
                    const totalHours = Number(rowData?.hours?.[ms] ?? 0);
                    const totalCost = Number(rowData?.costs?.[ms] ?? 0);
                    return (
                      <div
                        style={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'flex-end',
                          gap: 2,
                        }}
                      >
                        <div style={{ fontWeight: 800 }}>{totalHours.toFixed(2)}h</div>
                        <div style={{ fontWeight: 800, fontSize: 12 }}>{formatCurrency(totalCost)}</div>
                      </div>
                    );
                  }

                  const value = getDisplayHours(rowData, ms);
                  const monthLocked = isPastMonthLocked(ms);
                  const cost = formatCost(getRateForMonth(rowData, ms), value);
                  const baseMonthCapacity = workableHoursByMonthStart.get(ms);
                  const ftePercent = Number(userFteById.get(Number(rowData?.userId)) ?? 100);
                  const fteScale = Number.isFinite(ftePercent) ? Math.max(0, ftePercent) / 100 : 1;
                  const monthCapacity =
                    Number.isFinite(baseMonthCapacity) && baseMonthCapacity !== null
                      ? Number(baseMonthCapacity) * fteScale
                      : baseMonthCapacity;
                  const committedHours = Number(rowData?.committedHours?.[ms] ?? 0);
                  const committedPct =
                    Number.isFinite(monthCapacity) && monthCapacity > 0
                      ? Math.round((Math.max(0, committedHours) / monthCapacity) * 100)
                      : null;
                  const committedLabel =
                    Number.isInteger(committedPct) ? `${committedPct}% util` : null;

                  return (
                    <div
                      style={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'flex-end',
                        gap: 2,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                        {(() => {
                          const inputNode = (
                            <InputNumber
                              size="sm"
                              min={0}
                              step={0.25}
                              precision={2}
                              value={value}
                              onFocus={(e) => {
                                // Select-all so typing replaces the whole value (prevents leading 0 sticking around).
                                queueMicrotask(() => e?.target?.select?.());
                              }}
                              onClick={(e) => {
                                // Clicking into the input should also select-all.
                                queueMicrotask(() => e?.target?.select?.());
                              }}
                              onChange={(v) => onChangeCell(rowData, ms, v)}
                              onBlur={() => commitCell(rowData, ms)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  commitCell(rowData, ms);
                                }
                              }}
                              disabled={savingAll || monthLocked}
                              style={{ width: 120 }}
                            />
                          );

                          if (!monthLocked) return inputNode;

                          return (
                            <Whisper
                              trigger="hover"
                              placement="top"
                              speaker={<Tooltip>Cannot edit past forecasts</Tooltip>}
                            >
                              <span style={{ position: 'relative', display: 'inline-flex', width: 120 }}>
                                {inputNode}
                                <button
                                  type="button"
                                  aria-label="Cannot edit past forecasts"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                  }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    registerPastForecastUnlockAttempt();
                                  }}
                                  style={{
                                    position: 'absolute',
                                    inset: 0,
                                    border: 'none',
                                    background: 'transparent',
                                    padding: 0,
                                    margin: 0,
                                    cursor: 'not-allowed',
                                  }}
                                />
                              </span>
                            </Whisper>
                          );
                        })()}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: '#666',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          gap: 10,
                        }}
                      >
                        {committedLabel ? <span>{committedLabel}</span> : null}
                        <span>{cost}</span>
                      </div>
                    </div>
                  );
                }}
              </Cell>
            </Column>
          ))}

          <Column width={90} fixed="right" align="right">
            <HeaderCell>Total (h)</HeaderCell>
            <Cell>
              {(rowData) => {
                const getWindowServerTotal = (row) =>
                  monthStartsForTable.reduce((sum, ms) => sum + Number(row?.hours?.[ms] ?? 0), 0);

                const getWindowEffectiveTotal = (row) =>
                  monthStartsForTable.reduce((sum, ms) => sum + Number(getEffectiveHours(row, ms) ?? 0), 0);

                const getDisplayedTotal = (row) => {
                  if (isTotalRow(row)) return monthStartsForTable.reduce((sum, ms) => sum + Number(row?.hours?.[ms] ?? 0), 0);
                  return getWindowEffectiveTotal(row);
                };

                let total;
                if (!allTimeTotals) {
                  total = getDisplayedTotal(rowData);
                } else if (isTotalRow(rowData)) {
                  const baseOverall = Number(allTimeTotals?.totalHours ?? 0);
                  const deltaOverall = rows
                    .filter((r) => !isTotalRow(r))
                    .reduce((sum, r) => sum + (getWindowEffectiveTotal(r) - getWindowServerTotal(r)), 0);
                  total = baseOverall + deltaOverall;
                } else {
                  const uid = Number(rowData?.userId);
                  if (!allTimeTotalHoursByUserId.has(uid)) {
                    total = getDisplayedTotal(rowData);
                  } else {
                    const baseUser = Number(allTimeTotalHoursByUserId.get(uid) ?? 0);
                  const deltaUser = getWindowEffectiveTotal(rowData) - getWindowServerTotal(rowData);
                    total = baseUser + deltaUser;
                  }
                }

                return (
                  <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    <span
                      style={{
                        fontVariantNumeric: 'tabular-nums',
                        fontWeight: isTotalRow(rowData) ? 800 : 700,
                      }}
                    >
                      {total.toFixed(2)}h
                    </span>
                  </div>
                );
              }}
            </Cell>
          </Column>
        </Table>

        <Modal
          open={pastForecastOverrideModalOpen}
          onClose={() => setPastForecastOverrideModalOpen(false)}
          size="xs"
        >
          <Modal.Header>
            <Modal.Title>Override past forecast lock?</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Message type="warning" showIcon>
              Are you sure you want to override past forecast locking for this session?
            </Message>
          </Modal.Body>
          <Modal.Footer>
            <Button
              appearance="primary"
              color="red"
              onClick={() => {
                setPastForecastOverrideEnabled(true);
                pastForecastUnlockAttemptCountRef.current = 0;
                setPastForecastOverrideModalOpen(false);
              }}
            >
              Override
            </Button>
            <Button
              appearance="subtle"
              onClick={() => {
                setPastForecastOverrideModalOpen(false);
              }}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal
          open={removeSupportOpen}
          onClose={() => {
            setRemoveSupportOpen(false);
            setRemoveSupportUser(null);
          }}
          size="xs"
        >
          <Modal.Header>
            <Modal.Title>Remove support</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Message type="warning" showIcon>
              This will remove the user from the supports list.
            </Message>
            <div style={{ marginTop: 10, fontSize: 13 }}>
              Remove <b>{removeSupportUser?.userName || 'this user'}</b>?
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              appearance="primary"
              color="red"
              onClick={confirmRemoveSupport}
              loading={setSupporters.isPending}
              disabled={setSupporters.isPending}
            >
              Remove
            </Button>
            <Button
              appearance="subtle"
              onClick={() => {
                setRemoveSupportOpen(false);
                setRemoveSupportUser(null);
              }}
              disabled={setSupporters.isPending}
            >
              Cancel
            </Button>
          </Modal.Footer>
        </Modal>

        <div style={{ marginTop: 10, fontSize: 12, color: '#666' }}>
          Enter hours per user per month. Totals are shown in the last row. The right-most Total column shows all-time totals (with unsaved edits reflected for displayed months).
        </div>

        <div
          style={{
            marginTop: 10,
            paddingTop: 10,
            borderTop: '1px solid #eee',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: 22,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Button appearance="subtle" size="sm" onClick={() => setPrintMode(true)} disabled={printMode}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <i className="bi bi-printer" aria-hidden="true" />
                <span style={{ fontWeight: 700 }}>Print</span>
              </span>
            </Button>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 22 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
              <div style={{ fontSize: 12, color: '#666', fontWeight: 700 }}>All-time hours</div>
              <div style={{ fontSize: 18, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
                {Number(allTimeTotals?.totalHours ?? 0).toFixed(2)}h
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
              <div style={{ fontSize: 12, color: '#666', fontWeight: 700 }}>All-time cost</div>
              <div style={{ fontSize: 18, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
                {formatCurrency(allTimeTotals?.totalCost)}
              </div>
            </div>
          </div>
        </div>
        </Panel>
      </div>

      <div className="ppc-print-area" aria-hidden={!printMode}>
        <div className="ppc-print-header">
          <div style={{ fontSize: 18, fontWeight: 800 }}>Hour Forecast</div>
          <div style={{ marginTop: 2, color: '#444' }}>
            Activity: <span style={{ fontWeight: 700 }}>{selectedActivityLabel || '—'}</span>
          </div>
          <div style={{ marginTop: 2, color: '#444' }}>
            Task: <span style={{ fontWeight: 700 }}>{task?.name || task?.taskName || '—'}</span>
          </div>
          <div style={{ marginTop: 6, fontSize: 12, color: '#666' }}>
            Range: {rangeLabel || '—'}{filterMonthsWithHours ? ' (filtered)' : ''} • Printed{' '}
            {new Date().toLocaleString()}
          </div>
        </div>

        <table className="ppc-print-table">
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>User</th>
              <th style={{ textAlign: 'left' }}>Role</th>
              <th style={{ textAlign: 'right' }}>Rate</th>
              {monthStartsForTable.map((ms) => (
                <th key={ms} style={{ textAlign: 'right' }}>
                  {formatMonthLabel(ms)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const totalRow = isTotalRow(row);
              const roleLabel = totalRow ? '—' : row?.role === 'lead' ? 'Lead' : 'Support';

              return (
                <tr key={`${row?.userId}`} className={totalRow ? 'ppc-print-total-row' : undefined}>
                  <td style={{ textAlign: 'left', fontWeight: totalRow ? 800 : 600 }}>{row?.userName || '—'}</td>
                  <td style={{ textAlign: 'left' }}>{roleLabel}</td>
                  <td style={{ textAlign: 'right' }}>
                    {(() => {
                      if (totalRow) return '—';
                      const segs = buildRateSegments(monthStartsForTable, (ms) => getRateForMonth(row, ms));
                      const unique = Array.from(
                        new Set(
                          segs
                            .map((s) => Number(s.rate))
                            .filter((n) => Number.isFinite(n))
                            .map((n) => n.toFixed(2))
                        )
                      );
                      if (unique.length <= 1) return formatRate(row?.rate);
                      return unique.map((n) => formatRate(Number(n))).join(' / ');
                    })()}
                  </td>
                  {monthStartsForTable.map((ms) => {
                    const h = totalRow ? Number(row?.hours?.[ms] ?? 0) : Number(getEffectiveHours(row, ms) ?? 0);
                    const c =
                      totalRow
                        ? Number(row?.costs?.[ms] ?? 0)
                        : Number(getRateForMonth(row, ms) ?? 0) * Number(h ?? 0);
                    return (
                      <td key={ms} style={{ textAlign: 'right' }}>
                        <div style={{ fontVariantNumeric: 'tabular-nums' }}>{Number(h ?? 0).toFixed(2)}h</div>
                        <div style={{ fontSize: 11, color: '#555' }}>{formatCurrency(c)}</div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="ppc-print-summary">
          <div>
            Displayed hours:{' '}
            <span style={{ fontWeight: 800 }}>{Number(printDisplayedTotals?.displayedHours ?? 0).toFixed(2)}h</span>
          </div>
          <div>
            Displayed cost: <span style={{ fontWeight: 800 }}>{formatCurrency(printDisplayedTotals?.displayedCost)}</span>
          </div>
          <div>
            Total hours: <span style={{ fontWeight: 800 }}>{Number(allTimeTotals?.totalHours ?? 0).toFixed(2)}h</span>
          </div>
          <div>
            Total cost: <span style={{ fontWeight: 800 }}>{formatCurrency(allTimeTotals?.totalCost)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
